import { ChangeDetectionStrategy, Component, forwardRef, OnInit, Inject, ViewChild, HostListener } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { NgxActivityEditComponent } from '../ngx-activity-edit/ngx-activity-edit.component';
import { NgxGoToastService } from '@go-modules/services/go-toast/go-toast.service';
import { GoToastStatusType } from '@go-modules/enums/go-toast-status-type';
import { GoDialogRef } from '@go-modules/services/go-dialog-ref/go-dialog-ref';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { ConfirmDialogComponent } from '@go-modules/components/dialogs/confirm-dialog/confirm-dialog.component';
import { GO_SIDEPANEL_DATA } from '@go-modules/services/go-sidepanel/component/go-sidepanel.component';
import { FocusOnFirstInvalidFieldModule } from '@go-modules/directives/focus-on-first-invalid-field/focus-on-first-invalid-field.module';
import { activityToken } from 'go-modules/models/activity/activity.factory';
import { selectedServiceToken, SelectedService } from 'go-modules/services/selected/selected.service';
import { ActivityFormService } from '../activity-form/activity-form.service';
import { ACTIVITY_FORM_CONFIG } from '../activity-form/activity-form-config';
import { ActivityModel } from 'go-modules/models/activity/activity.schema';
import { EventService } from '@go-modules/services/event/event.service';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';
import { ActivityEditModeService } from '@go-modules/services/activity-edit-mode/activity-edit-mode.service';
import { Activity } from '@go-modules/interfaces/activity';
import { ActivityEditAccessibilityAnnouncerService } from '../activity-edit-accessibility-announcer.service';

@Component({
	selector: 'ngx-activity-edit-panel',
	standalone: true,
	imports: [
		CommonModule,
		TranslateModule,
		forwardRef(() => NgxActivityEditComponent),
		FocusOnFirstInvalidFieldModule
	],
	providers: [ActivityFormService, ActivityEditAccessibilityAnnouncerService],
	templateUrl: './ngx-activity-edit-panel.component.html',
	styleUrls: ['./ngx-activity-edit-panel.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class NgxActivityEditPanelComponent implements OnInit {
	@ViewChild(forwardRef(() => NgxActivityEditComponent))
	private activityEditComponent?: NgxActivityEditComponent;
	public activity: ActivityModel;
	public headerOptions: {
		editMode: boolean;
		hideName?: boolean;
	};

	public saving$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public editMode: boolean = false;

	constructor (
		@Inject(GO_SIDEPANEL_DATA) public data: {
			options: {
				inputs: {
					activity: ActivityModel;
					headerOptions: {
						editMode: boolean;
						hideName?: boolean;
					};
				};
			};
		},
		private dialogRef: GoDialogRef,
		private translate: TranslateService,
		private ngxGoToastService: NgxGoToastService,
		private matDialog: MatDialog,
		private activityService: NgxActivityService,
		private eventService: EventService,
		public accessibilityAnnouncer: ActivityEditAccessibilityAnnouncerService,
		@Inject(activityToken) private ActivityModel,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		public activityFormService: ActivityFormService,
		public activityEditModeService: ActivityEditModeService
	) {}

	public ngOnInit (): void {
		this.activity = this.ActivityModel.model(structuredClone(this.data.options.inputs.activity), true);
		this.headerOptions = this.data.options.inputs.headerOptions;
		this.editMode = this.headerOptions.editMode;
		this.activityFormService.initializeForm(this.activity);
	}

	@HostListener('keydown', ['$event'])
	public onKeyDown (event: KeyboardEvent): void {
		if (event.key !== 'Escape') {
			return;
		}

		event.preventDefault();
		event.stopPropagation();
		this.cancel();
	}

	public getPrimaryButtonText (): string {
		if (this.editMode) {
			return 'common_save-and-close';
		}
		return 'activity-editor-panel-header_create';
	}

	public save (): Observable<void> {
		if (!this.activityFormService.isFormValid()) {
			return of(void(0));
		}

		// To duplicate activities, we need to set it to dirty.
		if (this.activity.is_duplicate) {
			this.activityFormService.markFormAsDirty();
		}

		if (!this.activityFormService.isFormDirty()) {
			this.dialogRef.close();
			return of(void(0));
		}

		this.saving$.next(true);

		if (this.editMode) {
			if (this.changesRequireWarning()) {
				this.matDialog.open(ConfirmDialogComponent, {
					data: {
						title: this.translate.instant('modal-activity-edit-warning_edit-activity'),
						message: this.translate.instant('modal-activity-edit-warning_warning-text', {
							total: this.activity.usage
						}),
						confirmText: this.translate.instant('common_continue')
					}
				}).afterClosed().subscribe((confirmed: boolean) => {
					if (!confirmed) {
						this.saving$.next(false);
						return;
					}
					this.updateActivity();
				});
			} else {
				this.updateActivity();
			}
		} else {
			this.createActivity();
		}
	}

	public cancel (): void {
		if (this.activityFormService.isFormDirty()) {
			this.matDialog.open(ConfirmDialogComponent, {
				data: {
					title: this.translate.instant('activity-settings_controller-unsaved-changes'),
					message: this.translate.instant('activity-settings_controller-message-2'),
					confirmText: this.translate.instant('common_discard-changes'),
					cancelText: this.translate.instant('common_cancel')
				}
			}).afterClosed().subscribe((confirmed: boolean) => {
				if (confirmed) {
					this.activityEditComponent?.resetMarkerSelection();
					this.activityEditComponent?.resetAiSelections();
					this.dialogRef.close();
					this.eventService.broadcast(EVENT_NAMES.ACTIVITY_EDIT_CANCEL_CONFIRMED);
				}
			});
		} else {
			this.activityEditComponent?.resetMarkerSelection();
			this.activityEditComponent?.resetAiSelections();
			this.dialogRef.close();
		}
	}

	private createActivity (): void {
		const formValue = this.activityFormService.getFormValue();
		Object.assign(this.activity, formValue);

		this.activityService.save(this.activity).subscribe({
			next: (result: Activity) => {
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.SUCCESS,
					message: 'folder-view_activity_added'
				});
				this.activity.activity_id = result.activity_id;
				const modeledActivity = this.ActivityModel.model(this.activity);
				this.dialogRef.close(modeledActivity);
			},
			error: () => {
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.ERROR,
					message: 'common_unknown-error_message'
				});
			},
			complete: () => {
				this.saving$.next(false);
			}
		});
	}

	private updateActivity (): void {
		const formValue = this.activityFormService.getFormValue();
		Object.assign(this.activity, formValue);

		// we dont create or update tasks here so remove property so they stay as is
		delete this.activity.tasks;
		this.activityService.update(this.activity).subscribe({
			next: (result: Activity) => {
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.SUCCESS,
					message: 'folder-view_activity_updated'
				});
				const modeledActivity = this.ActivityModel.model(result);
				if (this.selectedService.getActivity()?.activity_id === this.activity.activity_id) {
					this.syncActivities(modeledActivity);
				}
				this.dialogRef.close(modeledActivity);
			},
			error: () => {
				this.ngxGoToastService.createToast({
					type: GoToastStatusType.ERROR,
					message: 'common_unknown-error_message'
				});
			},
			complete: () => {
				this.saving$.next(false);
			}
		});
	}

	private changesRequireWarning (): boolean {
		if (!this.activity.usage || this.activity.usage < 1) {
			return false;
		}

		// List form fields that are safe to change without warning
		const safeChanges = ['name'];

		const form = this.activityFormService.getForm();
		// Check if any non-safe form controls are dirty
		return Object.keys(form.controls).some((controlName) => {
			const control = form.get(controlName);
			return !safeChanges.includes(controlName) && control?.dirty;
		});
	}

	private syncActivities (activity: Activity) {
		this.selectedService.setActivity(activity);

		if (!this.activityEditModeService.isEditMode()) {
			return;
		}

		this.activityEditModeService.syncOriginalActivity(activity);

		// Merge only the fields that were actually changed (dirty) in this panel's form
		// This preserves additional settings changes (like live_session_enabled, is_conversation)
		// that were made in other components but not saved via this panel
		const currentEdited = this.activityEditModeService.currentActivity();
		const mergedActivity = { ...currentEdited };
		const form = this.activityFormService.getForm();

		Object.keys(ACTIVITY_FORM_CONFIG).forEach((key) => {
			const control = form.get(key);
			if (control?.dirty && activity.hasOwnProperty(key)) {
				mergedActivity[key] = activity[key];
			}
		});
		this.activityEditModeService.updateEditedActivity(mergedActivity as Activity, { markAsChanged: false });
	}
}
